import { FC, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useEventCallback } from '@mui/material';

import {
  AuthenticatorMfaRequest,
  ConfirmAuthenticator,
  ConfirmAuthenticatorSerialized,
  ConfirmRecoveryCode,
  ConfirmRecoveryCodeSerialized,
  LoginResponse,
  LoginWithGoogle,
  SuccessfulLogin,
} from '@playq/octopus2-auth';
import { GenericFailure } from '@playq/services-shared';
import { Either } from '@playq/irt';

import { getTokenFromResponse } from '/component/Login/helpers';
import { authToolkit } from '/store/toolkits';
import { useAppDispatch } from '/store';
import { login as loginAction } from '/store/toolkits/auth/actions';
import { companyId } from '/constants';
import { services2 } from '/api';
import { history } from '/Router/history';
import { snackbarService } from '/common/snackbarService';
import { useMFAInput } from '/common/MFA';

import { IGoogleLoginRedirectHandlerDispatchProps, IGoogleLoginRedirectHandlerStateProps } from './types';

export const GoogleLoginRedirectHandler: FC<
  IGoogleLoginRedirectHandlerStateProps & IGoogleLoginRedirectHandlerDispatchProps
> = () => {
  const isLoggedIn = useSelector(authToolkit.selectors.loggedIn);
  const googleRedirectURL = useSelector(authToolkit.selectors.googleRedirectURL);

  const dispatch = useAppDispatch();

  const login = useEventCallback((payload: SuccessfulLogin) => dispatch(loginAction(payload)));

  const accessToken: string | undefined = useMemo(() => getTokenFromResponse(location.href), []);

  const onClose = () => {
    history.replace('/login');
  };

  const handleLoginError = useEventCallback((err: GenericFailure | Error) => {
    snackbarService.error(`Login failed, ${err.message}`);
    onClose();
  });

  const handleLoginWithMfa = (params: ConfirmAuthenticatorSerialized | ConfirmRecoveryCodeSerialized) => {
    const mfaReq = 'pass' in params ? new ConfirmAuthenticator(params) : new ConfirmRecoveryCode(params);
    services2.authService.confirmMfa(mfaReq).then((resData) => {
      resData.bifold((loginResponse) => {
        if (loginResponse instanceof SuccessfulLogin) {
          login(loginResponse);
        }
      }, handleLoginError);
    });
  };

  const [MFAInput, openMFAInput] = useMFAInput({
    withRecovery: true,
    onSubmit: handleLoginWithMfa,
    onClose,
  });

  useEffect(() => {
    if (isLoggedIn && googleRedirectURL) {
      history.replace(googleRedirectURL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  useEffect(() => {
    const invalidToken = accessToken === undefined;

    if (invalidToken && isLoggedIn) {
      history.replace('/');
    }

    if (invalidToken && !isLoggedIn) {
      history.replace('/login');
    }
  }, [accessToken, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) return;

    if (!accessToken) {
      console.error('Authentication failed. Received an invalid token from Google.');
      return;
    }

    const loginMetadata: LoginWithGoogle = new LoginWithGoogle({
      companyId,
      accessToken,
    });

    services2.authService.login(loginMetadata).then((resData: Either<GenericFailure, LoginResponse>) => {
      resData.bifold((loginResponse: LoginResponse) => {
        if (loginResponse instanceof SuccessfulLogin) {
          login(loginResponse);
        } else if (loginResponse instanceof AuthenticatorMfaRequest) {
          openMFAInput(loginResponse.token);
        }
      }, handleLoginError);
    });
  }, [accessToken, isLoggedIn, login, openMFAInput, handleLoginError]);

  return (
    <div className='ball-scale-multiple'>
      <div />
      <div />
      <div />
      {MFAInput}
    </div>
  );
};
